import { BotBase } from './botBase';

import type {
	CardObject,
	Command,
	GameState,
	Manager,
	PassCommand,
	PlayCommand,
} from '../lib';
import {
	assumeType,
	compareRank,
	GameAction,
	getCardObjectFromString,
	getCardsInHand,
	getRandomItem,
	getTrickWinner,
	makeNextStageCommand,
	Rank,
	RoundStage,
	Suit,
} from '../lib';

// Heuristic expert bot.
// Improves on MediumBot with:
// - Real safety analysis (isSafePlay): a card is safe if no opponent can still
//   beat it given the cards already played and the cards still outstanding.
// - Intentional low leading to shed dangerous high cards without winning.
// - Shoot-the-moon detection: when the bot is in a position to take all points,
//   it switches to collecting instead of dumping.

interface TrickContext {
	currentTrick: { leadPlayer: number; cardsPlayed: string[] } | undefined;
	winner: number | null;
	winningCard: CardObject | null;
	leadSuit: Suit | null;
}

function getTrickContext(gameState: GameState): TrickContext {
	const currentRound = gameState?.rounds?.at(-1);
	const currentTrick = currentRound?.tricks?.at(-1);
	const winner = currentTrick ? getTrickWinner(currentTrick) : null;
	const winningCard = winner !== null && currentTrick?.cardsPlayed[winner]
		? getCardObjectFromString(currentTrick.cardsPlayed[winner])
		: null;
	const leadSuit = currentTrick && currentTrick.cardsPlayed.length > 0
		? getCardObjectFromString(currentTrick.cardsPlayed[currentTrick.leadPlayer]).suit
		: null;

	return {
		currentTrick,
		winner,
		winningCard,
		leadSuit,
	};
}

function getPotentialPoints(card: CardObject): number {
	if (card.suit === Suit.Hearts) {
		return 1;
	}

	if (card.suit === Suit.Spades && card.rank === Rank.Queen) {
		return 13;
	}

	return 0;
}

// A card would win the current trick only if it is of the lead suit and higher
// than the current winning card. Sloughing off-suit can never win.
function willWinTrick(card: CardObject, ctx: TrickContext): boolean {
	if (!ctx.winningCard || !ctx.leadSuit) {
		return false;
	}

	if (card.suit !== ctx.leadSuit) {
		return false;
	}

	return compareRank(card.rank, ctx.winningCard.rank) > 0;
}

// Real safety check: a play is safe if it does not win the trick AND no later
// player still holds a higher card of the lead suit that could beat it.
function isSafePlay(card: CardObject, gameState: GameState, position: number, ctx: TrickContext): boolean {
	if (willWinTrick(card, ctx)) {
		return false;
	}

	if (!ctx.leadSuit) {
		return true;
	}

	if (card.suit !== ctx.leadSuit) {
		return true;
	}

	return getHigherOutstandingCount(gameState, card, position) === 0;
}

// Count higher cards of the same suit still held by players who act after us.
function getHigherOutstandingCount(gameState: GameState, card: CardObject, position: number): number {
	const currentRound = gameState?.rounds?.at(-1);
	const playersWhoPlayed = new Set<number>();

	if (currentRound) {
		const trick = currentRound.tricks.at(-1);

		if (trick) {
			for (let p = 0; p < trick.cardsPlayed.length; ++p) {
				if (trick.cardsPlayed[p]) {
					playersWhoPlayed.add(p);
				}
			}
		}
	}

	let count = 0;

	for (const c of gameState.deck) {
		if (c.suit !== card.suit) {
			continue;
		}

		if (compareRank(c.rank, card.rank) <= 0) {
			continue;
		}

		const pileOwner = decodePilePlayer(c.pile);

		if (pileOwner !== null && playersWhoPlayed.has(pileOwner)) {
			continue;
		}

		if (c.pile.indexOf('trickWinnings') > -1) {
			continue;
		}

		if (pileOwner !== null && pileOwner !== position && !playersWhoPlayed.has(pileOwner)) {
			count++;
		}
	}

	return count;
}

function decodePilePlayer(pile: string): number | null {
	const ind = pile.search(/\d+/);

	if (ind > -1) {
		return parseInt(pile.substring(ind));
	}

	return null;
}

// Detect whether the bot is in a plausible shoot-the-moon position: it must have
// taken every point card scored so far and still hold the queen or have points.
function isShootingMoonViable(gameState: GameState, position: number): boolean {
	const currentRound = gameState?.rounds?.at(-1);

	if (!currentRound) {
		return false;
	}

	const totalHeartsScored = currentRound.heartPoints.reduce((a, b) => a + (b ?? 0), 0);
	const queenHolder = currentRound.queenSpadesPoints.findIndex((p) => (p ?? 0) > 0);
	const botHearts = currentRound.heartPoints[position] ?? 0;
	const botQueen = currentRound.queenSpadesPoints[position] ?? 0;

	const heartsTakenByOthers = totalHeartsScored - botHearts;
	const queenTakenByOthers = queenHolder !== -1 && queenHolder !== position;

	if (heartsTakenByOthers > 0 || queenTakenByOthers) {
		return false;
	}

	const botHasPoints = botHearts + botQueen > 0;

	// Only commit to a moon shot once the bot has already collected points this
	// round; never start one cold from zero (that would force winning tricks early
	// and rack up points unintentionally).
	return botHasPoints;
}

function hasQueen(cmd: PassCommand): boolean {
	return cmd.data.cards.some((c) => {
		const card = getCardObjectFromString(c);

		return card.suit === Suit.Spades && card.rank === Rank.Queen;
	});
}

function passDanger(cmd: PassCommand): number {
	return cmd.data.cards.reduce((acc, c) => {
		const card = getCardObjectFromString(c);
		let value = 0;

		if (card.suit === Suit.Spades && card.rank === Rank.Queen) {
			value += 13;
		}

		if (card.rank === Rank.Ace) {
			value += 5;
		}

		if (card.rank === Rank.King) {
			value += 3;
		}

		if (card.suit === Suit.Hearts) {
			value += 1;
		}

		return acc + value;
	}, 0);
}

function getBetterPassCandidate(previousCandidate: PassCommand, nextCandidate: PassCommand): PassCommand {
	const prevQueen = hasQueen(previousCandidate);
	const nextQueen = hasQueen(nextCandidate);

	if (prevQueen && !nextQueen) {
		return previousCandidate;
	}

	if (nextQueen && !prevQueen) {
		return nextCandidate;
	}

	const prevDanger = passDanger(previousCandidate);
	const nextDanger = passDanger(nextCandidate);

	if (nextDanger > prevDanger) {
		return nextCandidate;
	}

	return previousCandidate;
}

function getPassCommand(commands: Command[]): Command {
	let candidateCommand: PassCommand = getRandomItem(commands) as PassCommand;

	if (commands.length > 1) {
		for (const command of commands) {
			assumeType<PassCommand>(command);
			candidateCommand = getBetterPassCandidate(candidateCommand, command);
		}
	}

	return candidateCommand;
}

function getTrickCommand(commands: Command[], gameState: GameState, position: number): Command | null {
	if (!commands || commands.length === 0) {
		return null;
	}

	const ctx = getTrickContext(gameState);
	const shootingMoon = isShootingMoonViable(gameState, position);
	const playCommands = commands.map((c) => {
		assumeType<PlayCommand>(c);

		return c;
	});
	const cardOf = (cmd: PlayCommand): CardObject => getCardObjectFromString(cmd.data.card);

	if (shootingMoon) {
		const winners = playCommands.filter((cmd) => willWinTrick(cardOf(cmd), ctx) || !ctx.leadSuit);
		const pool = winners.length > 0 ? winners : playCommands;

		pool.sort((a, b) => {
			const pa = getPotentialPoints(cardOf(a));
			const pb = getPotentialPoints(cardOf(b));

			if (pb !== pa) {
				return pb - pa;
			}

			return compareRank(cardOf(b).rank, cardOf(a).rank);
		});

		return pool[0] as PlayCommand;
	}

	if (ctx.leadSuit) {
		// 1. Safest dump: an off-lead-suit point card that provably cannot win.
		const safePointSlough = playCommands.find((cmd) => {
			const card = cardOf(cmd);

			return getPotentialPoints(card) > 0
				&& card.suit !== ctx.leadSuit
				&& isSafePlay(card, gameState, position, ctx);
		});

		if (safePointSlough) {
			return safePointSlough as PlayCommand;
		}

		// 2. Aggressive dump: any point card that does not currently win the
		//    trick (matching MediumBot's point-avoidance, even if a later player
		//    could still beat it). Prefer off-suit, then highest points.
		const nonWinningPointCards = playCommands.filter((cmd) => {
			const card = cardOf(cmd);

			return getPotentialPoints(card) > 0 && !willWinTrick(card, ctx);
		});

		if (nonWinningPointCards.length > 0) {
			nonWinningPointCards.sort((a, b) => {
				const ca = cardOf(a);
				const cb = cardOf(b);

				if (ca.suit !== ctx.leadSuit && cb.suit === ctx.leadSuit) {
					return -1;
				}

				if (ca.suit === ctx.leadSuit && cb.suit !== ctx.leadSuit) {
					return 1;
				}

				const pa = getPotentialPoints(ca);
				const pb = getPotentialPoints(cb);

				if (pb !== pa) {
					return pb - pa;
				}

				return compareRank(cb.rank, ca.rank);
			});

			return nonWinningPointCards[0] as PlayCommand;
		}
	}

	if (ctx.leadSuit) {
		const safeNonWinners = playCommands.filter((cmd) => {
			const card = cardOf(cmd);

			return card.suit === ctx.leadSuit && isSafePlay(card, gameState, position, ctx);
		});

		if (safeNonWinners.length > 0) {
			// Shed the most dangerous (highest) card that cannot win.
			safeNonWinners.sort((a, b) => compareRank(cardOf(b).rank, cardOf(a).rank));

			return safeNonWinners[0] as PlayCommand;
		}

		// No provably-safe card; still avoid winning by playing the highest
		// lead-suit card that does not currently beat the winning card.
		const nonWinners = playCommands.filter((cmd) => {
			const card = cardOf(cmd);

			return card.suit === ctx.leadSuit && !willWinTrick(card, ctx);
		});

		if (nonWinners.length > 0) {
			nonWinners.sort((a, b) => compareRank(cardOf(b).rank, cardOf(a).rank));

			return nonWinners[0] as PlayCommand;
		}
	}

	if (!ctx.leadSuit) {
		// Leading: void a short non-point suit fast so we can duck later.
		const nonPointLeads = playCommands.filter((cmd) => getPotentialPoints(cardOf(cmd)) === 0);
		const pool = nonPointLeads.length > 0 ? nonPointLeads : playCommands;

		const hand = getCardsInHand(gameState.deck, position);
		const suitCount = (cmd: PlayCommand): number => hand.filter((c) => c.suit === cardOf(cmd).suit).length;

		// Prefer leading the shortest suit, then the lowest card of it.
		pool.sort((a, b) => {
			const sc = suitCount(a) - suitCount(b);

			if (sc !== 0) {
				return sc;
			}

			return compareRank(cardOf(a).rank, cardOf(b).rank);
		});

		return pool[0] as PlayCommand;
	}

	playCommands.sort((a, b) => compareRank(cardOf(a).rank, cardOf(b).rank));

	return playCommands[0] as PlayCommand;
}

export function getHardBotCommand(game: Manager, position: number, autoAdvanceRound = false): Command | null {
	const validCommands = game.getValidActions(position);

	switch (game.gameState.roundStage) {
		case RoundStage.TrickEnd:
			return makeNextStageCommand();
		case RoundStage.RoundEnd:
			if (autoAdvanceRound) {
				return makeNextStageCommand();
			}

			return getRandomItem(validCommands);
		case RoundStage.Trick:
			return getTrickCommand(validCommands, game.gameState, position);
		case RoundStage.Pass:
			return getPassCommand(validCommands);
		case RoundStage.PassEnd:
			if (autoAdvanceRound) {
				return makeNextStageCommand();
			}

			break;
		case RoundStage.Deal:
			if (autoAdvanceRound) {
				return {
					command: GameAction.Deal,
				} as Command;
			}

			break;
		case RoundStage.GameOver:
			if (autoAdvanceRound) {
				return {
					command: GameAction.NewGame,
				} as Command;
			}

			break;
		default:
			return getRandomItem(validCommands);
	}

	return null;
}

export class HardBot extends BotBase {
	public getCommand(game: Manager): Command | null {
		return getHardBotCommand(game, this.position, this.autoAdvanceRound);
	}
}
